import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { useConfig } from '@/contexts/ConfigContext';
import toast from 'react-hot-toast';
import { ArrowLeft, FileText, Bug, MessageSquare, ShieldAlert, X } from 'lucide-react';
import { clsx } from 'clsx';
import type { ItemType, ItemPriority, ItemUpdate } from '@/types';

const typeOptions: { value: ItemType; label: string; description: string; icon: typeof FileText }[] = [
  {
    value: 'feedback',
    label: 'Feedback',
    description: 'Share thoughts or suggestions about the product',
    icon: MessageSquare,
  },
  {
    value: 'requirement',
    label: 'Feature Request',
    description: 'Request a new feature or capability',
    icon: FileText,
  },
  {
    value: 'bug',
    label: 'Bug Report',
    description: 'Report something that is not working correctly',
    icon: Bug,
  },
];

const priorityOptions: { value: ItemPriority; label: string; description: string }[] = [
  { value: 'low', label: 'Low', description: 'Nice to have, no urgency' },
  { value: 'medium', label: 'Medium', description: 'Important but not blocking' },
  { value: 'high', label: 'High', description: 'Significant impact on work' },
  { value: 'critical', label: 'Critical', description: 'Blocking issue, needs immediate attention' },
];

export function EditItem() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { config } = useConfig();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<ItemUpdate>({
    title: '',
    description: '',
    priority: 'medium',
    tags: [],
  });
  const [itemType, setItemType] = useState<ItemType>('feedback');

  const availableTags = config.available_tags || [];

  const { data: item, isLoading } = useQuery({
    queryKey: ['item', id],
    queryFn: () => apiClient.getItem(id!),
    enabled: !!id,
  });

  // Check if user can edit this item
  const canEdit = user?.is_admin || item?.created_by === user?.id;

  // Populate form when item loads
  useEffect(() => {
    if (item) {
      setFormData({
        title: item.title,
        description: item.description,
        priority: item.priority,
        tags: item.tags || [],
      });
      setItemType(item.type);
    }
  }, [item]);

  const updateMutation = useMutation({
    mutationFn: (data: ItemUpdate) => apiClient.updateItem(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['item', id] });
      queryClient.invalidateQueries({ queryKey: ['all-items'] });
      toast.success('Item updated successfully');
      navigate(`/items/${id}`);
    },
    onError: (error: any) => {
      if (error?.response?.status === 403) {
        toast.error('You do not have permission to edit this item');
      } else {
        toast.error('Failed to update item');
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title?.trim() || !formData.description?.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    updateMutation.mutate(formData);
  };

  const toggleTag = (tag: string) => {
    const currentTags = formData.tags || [];
    if (currentTags.includes(tag)) {
      setFormData({
        ...formData,
        tags: currentTags.filter((t) => t !== tag),
      });
    } else {
      setFormData({
        ...formData,
        tags: [...currentTags, tag],
      });
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData({
      ...formData,
      tags: formData.tags?.filter((t) => t !== tagToRemove) || [],
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Item not found</p>
        <Link to="/items" className="mt-4 text-primary-600 dark:text-primary-400 hover:underline">
          Back to items
        </Link>
      </div>
    );
  }

  // Show access denied if user can't edit
  if (!canEdit) {
    return (
      <div className="max-w-2xl mx-auto">
        <Link
          to={`/items/${id}`}
          className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-6"
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back to item
        </Link>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8">
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
              <ShieldAlert className="h-8 w-8 text-red-600 dark:text-red-400" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Access Denied
            </h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-md">
              You do not have permission to edit this item. Only the item creator or administrators can make changes.
            </p>
            <Link
              to={`/items/${id}`}
              className="mt-6 px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700"
            >
              Return to Item
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Get tags that are on the item but not in available_tags (legacy tags)
  const legacyTags = (formData.tags || []).filter((tag) => !availableTags.includes(tag));

  return (
    <div className="max-w-2xl mx-auto">
      {/* Back link */}
      <Link
        to={`/items/${id}`}
        className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-6"
      >
        <ArrowLeft className="mr-1 h-4 w-4" />
        Back to item
      </Link>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
            Edit Item
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Update your {itemType}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Type display (read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Type
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {typeOptions.map((option) => {
                const Icon = option.icon;
                const isSelected = itemType === option.value;
                return (
                  <div
                    key={option.value}
                    className={clsx(
                      'relative flex flex-col items-center p-4 rounded-lg border-2',
                      isSelected
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-gray-700 opacity-50'
                    )}
                  >
                    <Icon
                      className={clsx(
                        'h-8 w-8 mb-2',
                        isSelected
                          ? 'text-primary-600 dark:text-primary-400'
                          : 'text-gray-400'
                      )}
                    />
                    <span
                      className={clsx(
                        'text-sm font-medium',
                        isSelected
                          ? 'text-primary-700 dark:text-primary-300'
                          : 'text-gray-900 dark:text-white'
                      )}
                    >
                      {option.label}
                    </span>
                  </div>
                );
              })}
            </div>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Item type cannot be changed after creation
            </p>
          </div>

          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="title"
              value={formData.title || ''}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Brief summary of your item"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              id="description"
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Provide details..."
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Priority */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Priority
            </label>
            <select
              value={formData.priority || 'medium'}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value as ItemPriority })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              {priorityOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label} - {option.description}
                </option>
              ))}
            </select>
          </div>

          {/* Tags */}
          {(availableTags.length > 0 || legacyTags.length > 0) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Tags (optional)
              </label>

              {/* Available tags */}
              {availableTags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {availableTags.map((tag) => {
                    const isSelected = formData.tags?.includes(tag);
                    return (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => toggleTag(tag)}
                        className={clsx(
                          'inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
                          isSelected
                            ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-800 dark:text-primary-200 ring-2 ring-primary-500'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        )}
                      >
                        {tag}
                        {isSelected && (
                          <X className="ml-1.5 h-3.5 w-3.5" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Legacy tags (tags on the item that are not in available_tags) */}
              {legacyTags.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    Other tags on this item:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {legacyTags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="ml-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {formData.tags && formData.tags.length > 0 && (
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Selected: {formData.tags.join(', ')}
                </p>
              )}
            </div>
          )}

          {/* Submit */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Link
              to={`/items/${id}`}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
